import { ID } from 'shared/types/id';

import { InternalTreeItem, TreeItem, TreeItemID } from './types';

/**
 * Generates a unique path-based identifier by recursively traversing
 * the parent chain from the item to the root.
 *
 * @param itemId - The ID of the current item
 * @param parentPath - The path of the parent (empty string for root)
 * @returns Path-based identifier (e.g., "root/tag1/task1")
 */
export const getItemPath = (
  itemId: ID,
  parentPath: string = '',
): TreeItemID => {
  if (!parentPath) {
    return itemId;
  }

  return `${parentPath}/${itemId}`;
};

/**
 * Generates path-based identifiers for all items in a tree structure.
 * This is used during transformation to assign unique IDs to each tree position.
 *
 * @param items - Record of TreeItem objects with original IDs
 * @param rootId - The root item ID
 * @returns Record of InternalTreeItem objects with path-based IDs
 */
export const transformToInternalTreeItems = (
  items: Record<ID, TreeItem>,
  rootId: ID,
): Record<TreeItemID, InternalTreeItem> => {
  const result: Record<TreeItemID, InternalTreeItem> = {};
  // Source ids on the current path. An item may repeat under different parents, but never under itself.
  const pathSourceIds = new Set<ID>();

  const processItem = (itemId: ID, parentPath: TreeItemID = ''): TreeItemID => {
    const item = items[itemId];
    if (!item) {
      return parentPath;
    }

    const pathId = getItemPath(itemId, parentPath);

    // Recursively process children first to get their path IDs
    const childPathIds: TreeItemID[] = [];
    if (item.children) {
      pathSourceIds.add(itemId);
      item.children.forEach((childId) => {
        // A corrupt goal parent cycle would otherwise recurse until the stack overflows.
        if (pathSourceIds.has(childId)) {
          return;
        }
        const childPathId = processItem(childId, pathId);
        childPathIds.push(childPathId);
      });
      pathSourceIds.delete(itemId);
    }

    // Create InternalTreeItem with path-based ID and original sourceId
    result[pathId] = {
      ...item,
      id: pathId,
      sourceId: itemId,
      children: childPathIds.length ? childPathIds : undefined,
    };
    return pathId;
  };

  processItem(rootId);
  return result;
};
